// Pure parser for the inline `__ASK_USER_QUESTION__{json}` marker emitted by
// the lane worker when Claude's AskUserQuestion tool fires. Lives outside
// the React component so message-list rendering can call it without pulling
// in the AskUserQuestionCard chunk (buttons, state, ~8 KB of UI it only
// needs when a question card actually renders).

import { inlineJsonMarker } from './inlineJsonMarker'

export const ASK_USER_QUESTION_MARKER = '__ASK_USER_QUESTION__'

export interface AskQuestionOption {
  label: string
  description?: string
}

export interface AskQuestionItem {
  question: string
  header: string
  multiSelect: boolean
  options: AskQuestionOption[]
}

export interface AskUserQuestionPayload {
  question_id: string
  questions: AskQuestionItem[]
}

const askUserQuestionMarker = inlineJsonMarker<AskUserQuestionPayload>(ASK_USER_QUESTION_MARKER)

export const parseAskUserQuestion = askUserQuestionMarker.parse
export const splitAskUserQuestion = askUserQuestionMarker.split

// Text sent as Mark's message when he answers a card: the bare answer for a
// single question, else one `Header: answer` line per question. A typed
// answer replaces the picked option (or joins the picks on multi-select).
// Null until every question has an answer. Optional free-form `extra` text
// follows the answers under an `Anything else:` line.
export function composeAskAnswer(
  questions: AskQuestionItem[],
  selections: Record<number, string[]>,
  custom: Record<number, string>,
  extra = '',
): string | null {
  const answers: string[] = []
  for (let qi = 0; qi < questions.length; qi++) {
    const typed = (custom[qi] ?? '').trim()
    const picked = selections[qi] ?? []
    const parts = typed
      ? (questions[qi].multiSelect ? [...picked, typed] : [typed])
      : picked
    if (parts.length === 0) return null
    answers.push(parts.join(', '))
  }
  const body = questions.length === 1
    ? answers[0]
    : questions.map((q, qi) => `${q.header || q.question}: ${answers[qi]}`).join('\n')
  const more = extra.trim()
  return more ? `${body}\n\nAnything else: ${more}` : body
}

/** Index of the newest message carrying a question card, or -1. An unanswered
 * card above it has been replaced, so Mark never answers an old question. */
export function latestQuestionCardIndex(bodies: ReadonlyArray<string | null | undefined>): number {
  for (let i = bodies.length - 1; i >= 0; i--) {
    if (bodies[i]?.includes(ASK_USER_QUESTION_MARKER)) return i
  }
  return -1
}

/** Queued follow-ups do not answer a card. Explicit card answers are linked;
 * dispatched legacy answers still render correctly after an app update. */
export function isQuestionCardAnswered(
  messages: ReadonlyArray<{ id: string; direction: string; status: string; reply_to_message_id: string | null }>,
  cardIndex: number,
): boolean {
  const card = messages[cardIndex]
  if (!card) return false
  return messages.some((message, index) => message.direction === 'inbound' && (
    (message.reply_to_message_id === card.id && ['queued', 'dispatched', 'replied'].includes(message.status))
    || (index > cardIndex && message.reply_to_message_id === null
      && ['dispatched', 'replied'].includes(message.status))
  ))
}
